import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { MenuItem, Pagination, PaginationItem, Select } from '@mui/material';

import ChevronDownIcon from './assets/chevron-down.svg';
import useStyles from './styles';

interface Props {
    page: number;
    count: number;
    rowsPerPage?: number;
    rowsPerPageOptions?: number[] | null;
    onPageChange?: (page: number) => void;
    onRowsPerPageChange?: (rowsPerPage: number) => void;
    getPaginationHref?: (page: number | null) => string;
    defaultPage?: number;
    boundaryCount?: number;
    siblingCount?: number;
    className?: string;
    totalCountLabel?: string;
}

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

const Paginator: React.FC<Props> = (props) => {
    const { classes, cx } = useStyles();
    const {
        page,
        count,
        defaultPage = 1,
        boundaryCount = 1,
        siblingCount = 2,
        onPageChange,
        getPaginationHref,
        rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
        onRowsPerPageChange,
        totalCountLabel,
        className
    } = props;

    const { rowsPerPage = rowsPerPageOptions?.[0] ?? 20 } = props;

    const handlePageChange = useCallback(
        (event: React.ChangeEvent<{ value: unknown }>, page: number) => {
            event.preventDefault();
            onPageChange?.(page);
        },
        [onPageChange]
    );

    const handleRowsPerPageChange = useCallback(
        (event: React.ChangeEvent<{ value: number }>) => {
            event.preventDefault();
            onRowsPerPageChange?.(event.target.value);
        },
        [onRowsPerPageChange]
    );

    return (
        <div className={cx(classes.container, className)}>
            {rowsPerPageOptions === null || isEmpty(rowsPerPageOptions) ? null : (
                <div className={classes.rowsPerPageContainer}>
                    <Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange as never}
                        variant="outlined"
                        size="small"
                        classes={{
                            root: classes.rowsPerPageSelectRoot,
                            select: classes.rowsPerPageSelect,
                            icon: classes.rowsPerPageSelectIcon
                        }}
                        IconComponent={ChevronDownIcon}
                        SelectDisplayProps={{ ['aria-controls']: '__paginator_page_size__' }}
                        MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                            transformOrigin: { vertical: 'top', horizontal: 'center' },
                            classes: { list: classes.menuList },
                            className: classes.menu
                        }}
                    >
                        {rowsPerPageOptions.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                                classes={{ root: classes.menuItem, selected: classes.menuItemSelected }}
                            >
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                    <span className={classes.rowsPerPageLabel}>{'Rows per page'}</span>
                </div>
            )}
            {typeof count === 'number' && count >= 0 && totalCountLabel ? (
                <div className={classes.totalCountContainer}>
                    <span className={classes.totalCountLabel}>{totalCountLabel}</span>
                    <span className={classes.totalCountValue}>{count}</span>
                </div>
            ) : null}
            <div className={classes.paginationContainer}>
                <Pagination
                    page={page}
                    count={Math.ceil(count / rowsPerPage)}
                    defaultPage={defaultPage}
                    boundaryCount={boundaryCount}
                    siblingCount={siblingCount}
                    shape="rounded"
                    onChange={handlePageChange}
                    className={classes.pagination}
                    renderItem={(item) => {
                        const link =
                            getPaginationHref && item.page !== page
                                ? { component: Link, to: getPaginationHref(item.page) }
                                : {};
                        return (
                            <PaginationItem
                                {...link}
                                {...item}
                                variant={item.selected ? 'text' : 'outlined'}
                                classes={{
                                    root: classes.paginationItem,
                                    selected: classes.selected,
                                    previousNext: classes.previousNext
                                }}
                            />
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default Paginator;
